import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Link2, Gavel, Archive } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";
import { VERDICT_COLOR } from "./ClaimTracker";
import { EntityLinkManager } from "@/components/EntityLinkManager";

const NEXT = {
  new: ["researching", "closed"],
  researching: ["evidence_review", "resolved", "new", "closed"],
  evidence_review: ["resolved", "researching", "closed"],
  resolved: ["evidence_review", "closed"],
  closed: ["new", "researching"],
};

function Section({ title, children, action }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-tactical font-bold text-sm">{title}</h3>{action}
      </div>
      {children}
    </Card>
  );
}

export default function ClaimTrackerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canReview = user?.roles?.some((r) => ["super_admin", "editor"].includes(r));
  const [meta, setMeta] = useState(null);
  const [rec, setRec] = useState(null);
  const [linkModal, setLinkModal] = useState(null);
  const [verdictModal, setVerdictModal] = useState(false);
  const [verdictForm, setVerdictForm] = useState({ verdict: "", verdict_summary: "" });
  const [groups, setGroups] = useState([]);

  const load = useCallback(() => {
    api.get(`/claim-tracker/entries/${id}`).then((r) => setRec(r.data)).catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => {
    api.get("/claim-tracker/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/claim-tracker/groups").then((r) => setGroups(r.data)).catch(() => {});
    load();
  }, [load]);

  const vlabel = (v) => meta?.verdict_labels?.[v] || v;

  const saveField = async (patch, msg = "Saved") => {
    try { await api.patch(`/claim-tracker/entries/${id}`, patch); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const transition = async (target) => {
    try { await api.post(`/claim-tracker/entries/${id}/transition`, { target }); toast.success(`Status → ${target}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveVerdict = async () => {
    try {
      await api.post(`/claim-tracker/entries/${id}/verdict`, verdictForm);
      toast.success("Verdict updated"); setVerdictModal(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const archive = async () => {
    try { await api.delete(`/claim-tracker/entries/${id}`); toast.success("Archived"); nav("/command-center/claim-tracker"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openLink = async (type) => {
    const map = { source: "/sources", claim: "/claims", media: "/media/assets", intel: "/ghana-intel/records" };
    const key = { source: "linked_sources", claim: "linked_claims", media: "linked_media", intel: "linked_intel" }[type];
    try {
      const { data } = await api.get(map[type]);
      const selected = (rec[key] || []).map((x) => x.id);
      setLinkModal({ type, options: data, selected });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLinks = async () => {
    try {
      await api.post(`/claim-tracker/entries/${id}/links`, { link_type: linkModal.type, ids: linkModal.selected });
      toast.success("Links updated"); setLinkModal(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSel = (v) => setLinkModal((m) => ({ ...m, selected: m.selected.includes(v) ? m.selected.filter((x) => x !== v) : [...m.selected, v] }));

  if (!rec) return <div className="p-6 text-zinc-500">Loading…</div>;
  const label = (o, type) => type === "source" ? o.title : type === "claim" ? o.statement : type === "media" ? o.original_filename : o.title;

  return (
    <div className="p-6" data-testid="ct-detail">
      <button onClick={() => nav("/command-center/claim-tracker")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Political Claim Tracker</button>
      <PageHeader title={rec.speaker} subtitle={[rec.speaker_role, rec.party, rec.institution].filter(Boolean).join(" · ")}>
        {canReview && <Btn variant="danger" data-testid="ct-archive-btn" onClick={archive}><Archive className="h-4 w-4" /> Archive</Btn>}
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Claim">
            <p className="text-sm text-zinc-200 mb-3" data-testid="ct-claim-display">“{rec.claim_text}”</p>
            <Field label="Normalized summary">
              <textarea data-testid="ct-summary-edit" className={inputCls} rows={2} defaultValue={rec.claim_summary || ""}
                onBlur={(e) => e.target.value !== (rec.claim_summary || "") && saveField({ claim_summary: e.target.value }, "Summary saved")} />
            </Field>
          </Section>

          <Section title="Verdict & Status" action={canReview && <Btn variant="ghost" data-testid="ct-open-verdict" onClick={() => { setVerdictForm({ verdict: rec.verdict, verdict_summary: rec.verdict_summary || "" }); setVerdictModal(true); }}><Gavel className="h-4 w-4" /> Assign Verdict</Btn>}>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="label-caps text-zinc-500">Verdict</span>
              <span data-testid="ct-current-verdict" className={`font-mono-data uppercase text-sm ${VERDICT_COLOR[rec.verdict] || "text-zinc-400"}`}>{vlabel(rec.verdict)}</span>
              <span className="label-caps text-zinc-500 ml-4">Status</span>
              <span data-testid="ct-current-status" className="font-mono-data uppercase text-sm text-emerald-400">{rec.status}</span>
              {!rec.has_supporting_evidence && <span className="text-xs text-amber-400">(link a source/claim to enable a verdict)</span>}
            </div>
            {rec.verdict_summary && <p className="text-sm text-zinc-400 mb-3" data-testid="ct-verdict-summary">{rec.verdict_summary}</p>}
            {canReview ? (
              <div className="flex flex-wrap gap-2" data-testid="ct-transitions">
                {(NEXT[rec.status] || []).map((t) => (
                  <Btn key={t} variant="ghost" data-testid={`ct-transition-${t}`} onClick={() => transition(t)}>{t}</Btn>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-600">Verdict and status changes require an editor.</p>}
          </Section>

          <Section title="Linked Sources" action={<Btn variant="ghost" data-testid="ct-link-source" onClick={() => openLink("source")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_sources.length === 0 ? <p className="text-xs text-zinc-600">No sources linked.</p> :
              <ul className="space-y-1 text-sm">{rec.linked_sources.map((s) => <li key={s.id} className="text-zinc-300">• {s.title}</li>)}</ul>}
          </Section>

          <Section title="Linked Claims & Evidence" action={<Btn variant="ghost" data-testid="ct-link-claim" onClick={() => openLink("claim")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_claims.length === 0 ? <p className="text-xs text-zinc-600">No claims linked.</p> :
              <ul className="space-y-2 text-sm">{rec.linked_claims.map((c) => (
                <li key={c.id} className="text-zinc-300">• {c.statement}
                  <span className="text-xs text-zinc-500"> — {c.evidence?.length || 0} evidence{c.verdict ? `, verdict: ${c.verdict}` : ""}</span>
                </li>))}</ul>}
          </Section>

          <Section title="Linked Media" action={<Btn variant="ghost" data-testid="ct-link-media" onClick={() => openLink("media")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_media.length === 0 ? <p className="text-xs text-zinc-600">No media linked.</p> :
              <ul className="text-sm text-zinc-300">{rec.linked_media.map((m) => <li key={m.id}>• {m.original_filename} <span className="text-xs text-zinc-500">({m.visibility})</span></li>)}</ul>}
          </Section>

          <Section title="Linked Ghana Intelligence" action={<Btn variant="ghost" data-testid="ct-link-intel" onClick={() => openLink("intel")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_intel.length === 0 ? <p className="text-xs text-zinc-600">No intelligence records linked.</p> :
              <ul className="text-sm">{rec.linked_intel.map((r) => <li key={r.id} className="text-zinc-300 hover:text-white cursor-pointer" onClick={() => nav(`/command-center/ghana-intel/${r.id}`)}>• {r.title} <span className="text-xs text-zinc-500">({r.status})</span></li>)}</ul>}
          </Section>

          <EntityLinkManager linkType="claim_tracker" recordId={id} note="Explicitly link canonical Entity Profiles (speaker, party, institution, etc.). Free-text speaker/party/institution fields above are preserved and never auto-converted." />
        </div>

        <div className="space-y-4">
          <Section title="Details">
            <dl className="text-sm space-y-2">
              <div><dt className="label-caps text-zinc-500">Date made</dt><dd className="text-zinc-300">{rec.date_made || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Location / Platform</dt><dd className="text-zinc-300">{rec.location || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Topic</dt><dd className="text-zinc-300">{rec.topic || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Region</dt><dd className="text-zinc-300">{rec.region || "—"}</dd></div>
              <div>
                <dt className="label-caps text-zinc-500">Group</dt>
                <dd><select data-testid="ct-group-detail" className={inputCls} value={rec.group_id || ""} onChange={(e) => saveField({ group_id: e.target.value }, "Group updated")}>
                  <option value="">None</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select></dd>
              </div>
            </dl>
          </Section>

          <Section title="Verdict & Status History">
            <ul className="space-y-2 text-xs" data-testid="ct-audit">
              {rec.audit_history.length === 0 && <li className="text-zinc-600">No activity.</li>}
              {rec.audit_history.map((a) => (
                <li key={a.id} className="text-zinc-400">
                  <span className="font-mono-data text-emerald-400">{a.action}</span>
                  {a.after?.verdict && <span> → {vlabel(a.after.verdict)}</span>}
                  {a.after?.status && <span> → {a.after.status}</span>}
                  <span className="text-zinc-600"> · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      <Modal open={verdictModal} onClose={() => setVerdictModal(false)} title="Assign / Change Verdict">
        <div className="space-y-4">
          <Field label="Verdict">
            <select data-testid="ct-verdict-select" className={inputCls} value={verdictForm.verdict} onChange={(e) => setVerdictForm({ ...verdictForm, verdict: e.target.value })}>
              {(meta?.verdicts || []).map((v) => <option key={v} value={v}>{vlabel(v)}</option>)}
            </select>
          </Field>
          <Field label="Rationale / summary (never inferred from publication)">
            <textarea data-testid="ct-verdict-rationale" className={inputCls} rows={3} value={verdictForm.verdict_summary} onChange={(e) => setVerdictForm({ ...verdictForm, verdict_summary: e.target.value })} />
          </Field>
          {!rec.has_supporting_evidence && verdictForm.verdict !== "unverified" && <p className="text-xs text-amber-400">A verdict other than Unverified requires at least one linked source or Claims & Evidence record.</p>}
          <Btn data-testid="ct-verdict-save" onClick={saveVerdict} className="w-full justify-center">Save Verdict</Btn>
        </div>
      </Modal>

      <Modal open={!!linkModal} onClose={() => setLinkModal(null)} title={`Link ${linkModal?.type}`} wide>
        {linkModal && (
          <div className="space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-1" data-testid="ct-link-options">
              {linkModal.options.length === 0 && <p className="text-xs text-zinc-600">Nothing available. Create one in its own workspace first.</p>}
              {linkModal.options.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
                  <input type="checkbox" checked={linkModal.selected.includes(o.id)} onChange={() => toggleSel(o.id)} />
                  <span className="truncate">{label(o, linkModal.type)}</span>
                </label>
              ))}
            </div>
            <Btn data-testid="ct-save-links" onClick={saveLinks} className="w-full justify-center">Save Links</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
